import { useCallback, useRef } from 'react';
import {
  Button, Dialog, DialogActions, DialogContent, DialogTitle, Stack, Typography,
} from '@mui/material';
import { TimerOutlined } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/auth/useAuth';
import { IDLE_LIMIT_MS, useIdleTimeout } from '@/hooks/useIdleTimeout';

/** The limit in whole minutes, for the sentence that explains it. */
const LIMIT_MINUTES = Math.round(IDLE_LIMIT_MS / 60000);

/**
 * Signs the workspace out after a spell with nobody at the keyboard.
 *
 * <p>
 * Mounted inside the authenticated shell, so it exists only while there is a session to end.
 * </p>
 * <p>
 * The warning is the point of it, not a courtesy. This product has long forms — a quotation, a
 * package, an itinerary day — and ending a session without notice throws away work somebody is in
 * the middle of. A minute is enough to notice, finish a sentence, and press the button.
 * </p>
 */
export default function IdleSessionGuard() {
  const { isAuthenticated, logout } = useAuth();
  const navigate = useNavigate();

  // Signing out is asynchronous and the tick that triggered it may fire again before it finishes.
  // Without this the same timeout would call logout several times over.
  const leaving = useRef(false);

  const end = useCallback(async () => {
    if (leaving.current) return;
    leaving.current = true;

    try { await logout(); } catch { /* the session is ending either way */ }
    navigate('/login', { replace: true, state: { idle: true } });
  }, [logout, navigate]);

  const { warningAt, secondsLeft, markActive } = useIdleTimeout({
    enabled: isAuthenticated,
    onTimeout: end,
  });

  return (
    <Dialog open={warningAt !== null} maxWidth="xs" fullWidth>
      <DialogTitle sx={{ fontSize: 16.5, fontWeight: 800, pb: 1 }}>
        <Stack direction="row" alignItems="center" spacing={1}>
          <TimerOutlined sx={{ fontSize: 20, color: 'warning.main' }} />
          <span>Still there?</span>
        </Stack>
      </DialogTitle>

      <DialogContent>
        <Typography sx={{ fontSize: 13.5, color: 'text.secondary', lineHeight: 1.65 }}>
          You&rsquo;ve been away for {LIMIT_MINUTES} minutes, so we&rsquo;re about to sign you out to
          keep your workspace private.
        </Typography>

        <Typography sx={{ fontSize: 13.5, fontWeight: 700, mt: 1.5 }}>
          Signing out in {secondsLeft}s.
        </Typography>
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 2.5 }}>
        <Button onClick={end} color="inherit">Sign out now</Button>
        <Button
          variant="contained" onClick={markActive}
          sx={{ borderRadius: 2.5, fontWeight: 700 }}
        >
          Stay signed in
        </Button>
      </DialogActions>
    </Dialog>
  );
}
